import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'vikkygold_cart';

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveCart(items) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(loadCart);

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = useCallback((jewel) => {
    setItems(prev => {
      const existing = prev.find(i => i.jewelId === jewel.id);
      if (existing) {
        return prev.map(i =>
          i.jewelId === jewel.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, {
        jewelId: jewel.id,
        name: jewel.name,
        material: jewel.material,
        weight_g: jewel.weight_g,
        display_price: jewel.display_price,
        image_mime_type: jewel.image_mime_type,
        quantity: 1,
      }];
    });
  }, []);

  const removeItem = useCallback((jewelId) => {
    setItems(prev => prev.filter(i => i.jewelId !== jewelId));
  }, []);

  const updateQuantity = useCallback((jewelId, quantity) => {
    if (quantity < 1) return;
    setItems(prev =>
      prev.map(i => i.jewelId === jewelId ? { ...i, quantity } : i)
    );
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalValue = items.reduce((sum, i) => sum + (i.display_price || 0) * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, totalItems, totalValue }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
